import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Regions } from '../../../constants/region';
import { TranslationLocale } from '../../../i18n';
import { Regions as TranslatedRegions } from '../../../models/SystemStore/SystemStore';
import { getRegionsList } from '../../../stores/SystemStore';
import { getUiLocale } from '../../../utils/i18n';
import { JoyajoyImage, SvgIcon, TextInput } from '../../Molecules';
import { ActionSheetContainer } from '../Containers/ActionSheetContainer';

import styles from './PhoneCodePopup.module.css';

type PhoneCodePopupProps = {
  onPrimary: () => void;
  onCode: (selected: string) => void;
};

type FlattenedRegion = {
  id: string;
  country: string;
  code: string;
  image: string;
};

export const PhoneCodePopup = (props: PhoneCodePopupProps) => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [translatedRegions, setTranslatedRegions] = useState<TranslatedRegions>(
    {},
  );

  useEffect(() => {
    const fetchTranslatedRegions = async () => {
      try {
        const regions = await getRegionsList(
          getUiLocale(i18n.language as TranslationLocale),
        );
        setTranslatedRegions(regions);
      } catch (error) {
        console.error('Failed to fetch regions:', error);
      }
    };

    void fetchTranslatedRegions();
  }, [i18n.language]);

  const filteredAndSortedRegions = useMemo(() => {
    const filteredRegions = Regions.filter((region) => {
      if (!region.countryCodes?.length || !region.country) return false;

      const translatedName = translatedRegions[region.id] || region.country;
      const searchLower = searchTerm.toLowerCase();

      return (
        translatedName.toLowerCase().includes(searchLower) ||
        region.countryCodes.some((code) =>
          code.toString().includes(searchLower),
        ) ||
        region.country.toLowerCase().includes(searchLower)
      );
    });

    const flattenedRegions: FlattenedRegion[] = filteredRegions.flatMap(
      (region) =>
        region.countryCodes!.map((code) => ({
          id: `${region.id}-${code}`,
          country: translatedRegions[region.id] || region.country!,
          code: code,
          image: region.image,
        })),
    );

    return flattenedRegions.sort((a, b) =>
      a.country.localeCompare(b.country, i18n.language),
    );
  }, [translatedRegions, searchTerm, i18n.language]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  return (
    <ActionSheetContainer onPrimaryButtonClick={props.onPrimary}>
      <div className={styles.phoneNumber}>
        <div className={styles.titleRow}>
          <SvgIcon
            name={'Region'}
            width={24}
            height={24}
            stroke="black"
            className={styles.earthIcon}
          />
          <p className={styles.title}>{t('Country / Region')}</p>
        </div>
        <hr className={styles.dividerPage} />
        <div className={styles.searchBar}>
          <TextInput
            value={searchTerm}
            onChange={handleSearch}
            placeholder={t('Search countries...')}
            style={{
              backgroundColor: '#f0f0f0',
              border: '0',
              borderRadius: '8px 8px 8px 8px',
            }}
          />
          {/* <div className={styles.searchIcon}>     // this seems to be not very useful
            <SearchWhiteIcon
              className="search-icon"
              stroke={'#34A799'}
              fill={'#ffffff'}
              height={'42px'}
              width={'40px'}
            />
          </div> */}
        </div>
        <hr className={styles.dividerPage} />
        <div className={styles.scrollableContainer}>
          <div className={styles.codeList}>
            {filteredAndSortedRegions.length === 0 ? (
              <div className={styles.notFound}>{t('Not Found')}</div>
            ) : (
              filteredAndSortedRegions.map((item, index) => (
                <div key={item.id}>
                  <div
                    className={styles.codeItem}
                    onClick={() => props.onCode(`+${item.code}`)}
                  >
                    <JoyajoyImage
                      resource={{ uri: item.image || '' }}
                      style={{ width: 28, height: 28, marginRight: 10 }}
                    />
                    <span>{`${item.country} (+${item.code})`}</span>
                  </div>
                  {index < filteredAndSortedRegions.length - 1 && (
                    <hr className={styles.divider} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </ActionSheetContainer>
  );
};
